import React from 'react'
import { Form, InputNumber, Select, DatePicker } from 'antd'
import { rtlInputNumber, selectInput, columnTitle } from '../../EditSubStoDataPopup/edit-sub-sto.scss'
import moment from 'moment'
import { withFormContext, FormContextValue } from '../FormContext'

const { Item: FormItem } = Form

// ── constants ────────────────────────────────────────────────
const chargeFrequencyOptions = [
    { value: 'monthly', label: 'חודשי' },
    { value: 'weekly', label: 'שבועי' },
    { value: 'quarterly', label: 'רבעוני' },
    { value: 'half-yearly', label: 'חצי שנתי' },
    { value: 'yearly', label: 'שנתי' }
]

const currencyOptions = [
    { value: 'ILS', label: 'שקל חדש (₪)' },
    { value: 'USD', label: 'דולר אמריקאי ($)' },
    { value: 'EUR', label: 'אירו (€)' }
]

const stoPaymentsOptions = Array.from({ length: 60 }, (_, i) => ({
    value: i + 1,
    label: `${i + 1}`
}))

const chargeDomOptions = Array.from({ length: 28 }, (_, i) => ({
    value: i + 1,
    label: `${i + 1}`
}))

// ── helpers ──────────────────────────────────────────────────
const rightLabel = { labelCol: { style: { textAlign: 'right' as 'right' } } }

const digitParser = v => v.replace(/[^\d]/g, '')

const currencyFormatter = sign => v => `${sign} ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

const renderSelectOptions = options =>
    options.map(({ value, label }) => (
        <Select.Option key={value} value={value}>
            {label}
        </Select.Option>
    ))

// ── sub-components ───────────────────────────────────────────
const ChargeAmountField = ({
    getFieldDecorator,
    getFieldValue,
    currencySign,
    hasItems,
    itemsTotalSum,
    terminalType
}) => {
    const fmt = currencyFormatter(currencySign)
    const debitType = getFieldValue('debit_type')
    const isRequired = terminalType === 'mediwho1tok' && debitType === 'immediate_only'

    if (hasItems) {
        return (
            <FormItem label="סה״כ לחיוב" {...rightLabel}>
                <InputNumber
                    value={itemsTotalSum}
                    formatter={fmt}
                    parser={digitParser}
                    className={rtlInputNumber}
                    disabled
                />
            </FormItem>
        )
    }

    return (
        <FormItem label="סה״כ לחיוב" {...rightLabel}>
            {getFieldDecorator('charge_amount', {
                rules: [{ required: isRequired, message: 'יש להזין סכום' }]
            })(<InputNumber formatter={fmt} parser={digitParser} className={rtlInputNumber} disabled />)}
        </FormItem>
    )
}

// ── main component ───────────────────────────────────────────
const PaymentInfo = ({
    currencySign,
    items,
    itemsTotalSum,
    terminalType,
    defaultChargeDay,
    defaultChargeDate,
    onCurrencyChange,
    onChargeDateChange,
    onChargeDomChange,
    onVatPercentChange,
    getFieldDecorator,
    getFieldValue
}: {
    currencySign: string
    items: any[]
    itemsTotalSum: number
    terminalType: string
    defaultChargeDay: number
    defaultChargeDate: any
    onCurrencyChange: (value: any) => void
    onChargeDateChange: (date: any) => void
    onChargeDomChange: (day: any) => void
    onVatPercentChange: (vatPercent: number) => void
} & Pick<FormContextValue, 'getFieldDecorator' | 'getFieldValue'>) => {
    const hasItems = !!(items && items.length)

    return (
        <div>
            <div className={columnTitle}>פרטי חיוב</div>

            <ChargeAmountField
                getFieldDecorator={getFieldDecorator}
                getFieldValue={getFieldValue}
                currencySign={currencySign}
                hasItems={hasItems}
                itemsTotalSum={itemsTotalSum}
                terminalType={terminalType}
            />

            <FormItem label="מטבע" {...rightLabel}>
                {getFieldDecorator('currency_code', { initialValue: 'ILS' })(
                    <Select className={selectInput} onChange={onCurrencyChange}>
                        {renderSelectOptions(currencyOptions)}
                    </Select>
                )}
            </FormItem>

            <FormItem label="תדירות החיוב" {...rightLabel}>
                {getFieldDecorator('charge_frequency', { initialValue: 'monthly' })(
                    <Select className={selectInput}>{renderSelectOptions(chargeFrequencyOptions)}</Select>
                )}
            </FormItem>

            <FormItem label="אחוז מע״מ" {...rightLabel}>
                {getFieldDecorator('vat_percent', {
                    initialValue: 18,
                    rules: [{ required: true, type: 'number', min: 0, max: 100, message: 'ערך חייב להיות בין 0 ל-100' }]
                })(
                    <InputNumber
                        className={rtlInputNumber}
                        min={0}
                        max={100}
                        step={0.1}
                        formatter={v => `${v}%`}
                        parser={v => v.replace('%', '')}
                        onChange={onVatPercentChange}
                    />
                )}
            </FormItem>

            <FormItem label="תחילת החיובים" {...rightLabel}>
                {getFieldDecorator('first_charge_date', {
                    initialValue: defaultChargeDate,
                    rules: [{ required: true, message: 'יש לבחור תאריך' }]
                })(
                    <DatePicker
                        style={{ width: '100%', height: 34, border: 'none' }}
                        format="DD/MM/YYYY"
                        onChange={onChargeDateChange}
                        disabledDate={current => !!current && (current < moment().endOf('day') || current.date() > 28)}
                    />
                )}
            </FormItem>

            <FormItem label="יום חיוב בחודש" {...rightLabel}>
                {getFieldDecorator('charge_dom', {
                    initialValue: defaultChargeDay,
                    rules: [{ required: true, message: 'יש לבחור יום חיוב' }]
                })(
                    <Select style={{ width: '100%' }} onChange={onChargeDomChange}>
                        {renderSelectOptions(chargeDomOptions)}
                    </Select>
                )}
            </FormItem>

            <FormItem label="מספר תשלומים" {...rightLabel}>
                {getFieldDecorator('sto_payments_number', { initialValue: 9999 })(
                    <Select
                        style={{ width: '100%' }}
                        disabled={terminalType !== 'mediwhobk' && getFieldValue('debit_type') === 'immediate_only'}
                    >
                        {renderSelectOptions(stoPaymentsOptions)}
                        <Select.Option value={9999}>ללא הגבלה</Select.Option>
                    </Select>
                )}
            </FormItem>
        </div>
    )
}

export default withFormContext(PaymentInfo)
